// WellSelectionFilterMenu.js
// Shared filter menu component for both Inspections and Wells/Sampling tabs

import { useState, useMemo } from "react";
import Select from "react-select";
// Import shared filter defaults
import { samplingEventWellsFiltersInitialState } from "../../../store/forms/samplingEventForm/filter/samplingEventWellsFiltersState";

// Filter type configurations for different tabs
const FILTER_TYPE_CONFIG = {
  inspections: {
    statusField: "inspectionStatus",
    statusLabel: "Inspection Status",
    statusOptions: [
      { value: "all", label: "All Wells" },
      { value: "completed", label: "Completed Inspections" },
      { value: "incomplete", label: "Incomplete Inspections" },
    ],
  },
  sampling: {
    statusField: "samplingStatus",
    statusLabel: "Sampling Status",
    statusOptions: [
      { value: "all", label: "All Wells" },
      { value: "completed", label: "Completed Sampling" },
      { value: "incomplete", label: "Incomplete Sampling" },
    ],
  },
};

// Group assignment filter options (used by Wells/Sampling tab)
const GROUP_ASSIGNMENT_OPTIONS = [
  { value: "all", label: "All Wells" },
  { value: "assigned", label: "Group Assigned Wells" },
  { value: "unassigned", label: "Unassigned Wells" },
];

// Default filter and sort values from shared state
const defaultFilters = samplingEventWellsFiltersInitialState.filters;
const defaultSort = samplingEventWellsFiltersInitialState.sort;

/**
 * Shared FilterMenu component for wells filtering
 * @param {object} wellFilters - Current filter values from state
 * @param {function} setWellFilters - Action to update filters (calls applyFilters)
 * @param {object} wellSort - Current sort values from state
 * @param {function} setWellSort - Action to update sort (calls setSort)
 * @param {function} applyFilters - Action to apply both filters and sort
 * @param {function} resetFilters - Action to reset all filters (calls resetAll)
 * @param {function} closeFilterMenu - Action to close the filter menu
 * @param {object} styles - CSS module styles from parent
 * @param {string} filterType - Type of filter: 'inspections' | 'sampling' (default: 'inspections')
 * @param {Array} wells - Array of well objects to derive status options from
 * @param {boolean} showGroupFilter - Whether to show the group assignment filter (default: false)
 */
export const FilterMenu = ({
  wellFilters,
  setWellFilters,
  wellSort,
  setWellSort,
  applyFilters,
  resetFilters,
  closeFilterMenu,
  styles,
  filterType = "inspections",
  wells = [],
  showGroupFilter = false,
}) => {
  // Get configuration for this filter type
  const config =
    FILTER_TYPE_CONFIG[filterType] || FILTER_TYPE_CONFIG.inspections;

  // Derive unique well status options from the wells data
  const wellStatusOptions = useMemo(() => {
    if (!wells || wells.length === 0) return [];

    // Get unique non-empty status values from wells
    const uniqueStatuses = [
      ...new Set(wells.map((well) => well.status).filter(Boolean)),
    ];

    // Sort alphabetically and map to react-select format
    return uniqueStatuses.sort().map((status) => ({
      label: status,
      value: status,
    }));
  }, [wells]);

  // Local state for editing before applying
  const [tempFilters, setTempFilters] = useState(wellFilters);
  const [tempSort, setTempSort] = useState(wellSort);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setTempFilters((prev) => ({ ...prev, [name]: value }));
  };

  // Handler for react-select well status filter
  const handleWellStatusChange = (selectedOption) => {
    setTempFilters((prev) => ({
      ...prev,
      status: selectedOption ? selectedOption.value : "",
    }));
  };

  const handleSortChange = (e) => {
    const { name, value } = e.target;
    setTempSort((prev) => ({ ...prev, [name]: value }));
  };

  const handleApply = () => {
    applyFilters(tempFilters, tempSort);
    closeFilterMenu();
  };

  const handleReset = () => {
    // Reset to defaults from shared state
    setTempFilters(defaultFilters);
    setTempSort(defaultSort);
    resetFilters();
    closeFilterMenu();
  };

  return (
    <div className={styles.filterMenu}>
      <div className={styles.filterGroup}>
        <label htmlFor="wellId">Well ID:</label>
        <input
          type="text"
          id="wellId"
          name="wellId"
          value={tempFilters.wellId || ""}
          onChange={handleFilterChange}
          className={styles.filterInput}
          placeholder="Filter by Well ID"
        />
      </div>

      <div className={styles.filterGroup}>
        <label htmlFor="status">Well Status:</label>
        <Select
          inputId="status"
          name="status"
          value={
            tempFilters.status
              ? { label: tempFilters.status, value: tempFilters.status }
              : null
          }
          onChange={handleWellStatusChange}
          options={wellStatusOptions}
          isClearable
          placeholder="Select status..."
          className={styles.reactSelectContainer}
          classNamePrefix="reactSelect"
        />
      </div>

      <div className={styles.filterGroup}>
        <label htmlFor={config.statusField}>{config.statusLabel}:</label>
        <select
          id={config.statusField}
          name={config.statusField}
          value={tempFilters[config.statusField] || "all"}
          onChange={handleFilterChange}
          className={styles.filterInput}
        >
          {config.statusOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {showGroupFilter && (
        <div className={styles.filterGroup}>
          <label htmlFor="groupAssignment">Group Assignment:</label>
          <select
            id="groupAssignment"
            name="groupAssignment"
            value={tempFilters.groupAssignment || "all"}
            onChange={handleFilterChange}
            className={styles.filterInput}
          >
            {GROUP_ASSIGNMENT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className={styles.filterGroup}>
        <label htmlFor="sortField">Sort By:</label>
        <select
          id="sortField"
          name="field"
          value={tempSort.field || "wellId"}
          onChange={handleSortChange}
          className={styles.filterInput}
        >
          <option value="wellId">Well ID</option>
          <option value="siteName">Site Name</option>
          <option value="status">Well Status</option>
        </select>
      </div>

      <div className={styles.filterGroup}>
        <label htmlFor="sortDirection">Sort Direction:</label>
        <select
          id="sortDirection"
          name="direction"
          value={tempSort.direction || "asc"}
          onChange={handleSortChange}
          className={styles.filterInput}
        >
          <option value="asc">Ascending</option>
          <option value="desc">Descending</option>
        </select>
      </div>

      <div className={styles.filterActions}>
        <button className={styles.resetFilter} onClick={handleReset}>
          Reset
        </button>
        <button className={styles.applyFilter} onClick={handleApply}>
          Apply
        </button>
      </div>
    </div>
  );
};
